import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { Logo } from '@/components/Logo'
import type { SessionUser } from '@/lib/session-types'

interface InvitationLookup {
  email: string
  role: string
  message: string | null
  artistName: string
  artistSlug: string | null
}

interface AcceptResponse {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

/**
 * Crew invitation accept landing.
 * URL: /join?invite=TOKEN
 *
 * Looks up the invitation by token, shows the artist + role + personal message,
 * lets the invitee finish creating a free account (or just accept if they
 * already have one — but the API handles that case in /auth/invitations/accept).
 */
export function AcceptInvitationPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const token = params.get('invite') ?? ''

  const { data: invite, error: lookupError, isLoading } = useQuery<
    InvitationLookup,
    ApiClientError
  >({
    queryKey: ['invitation', token],
    enabled: !!token,
    queryFn: () =>
      apiFetch<InvitationLookup>(`/auth/invitations/${token}`, { skipAuth: true }),
  })

  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const data = await apiFetch<AcceptResponse>('/auth/invitations/accept', {
        method: 'POST',
        body: { token, displayName, password },
        skipAuth: true,
      })
      setSession(data)
      navigate('/crew/profile', { replace: true })
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!token) {
    return (
      <Centered>
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Missing invite token. Use the link in your invitation email.
        </div>
      </Centered>
    )
  }

  return (
    <Centered>
      {isLoading && (
        <div className="text-sm text-ovation-muted">Looking up invitation…</div>
      )}
      {lookupError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {lookupError.message}
        </div>
      )}
      {invite && (
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border border-ovation-ink/10 bg-white p-7 shadow-xl shadow-ovation-ink/5"
        >
          <div>
            <h1 className="font-display text-2xl tracking-tight">
              You've been invited
            </h1>
            <p className="mt-2 text-sm">
              <strong>{invite.artistName}</strong> has invited you to join their
              touring team as{' '}
              <strong className="capitalize">{invite.role.replace(/_/g, ' ')}</strong>.
            </p>
            {invite.message && (
              <blockquote className="mt-3 rounded-md border-l-4 border-ovation-accent bg-ovation-paper/40 p-3 text-sm italic text-ovation-muted">
                "{invite.message}"
              </blockquote>
            )}
          </div>

          <div className="rounded-md bg-ovation-paper/40 p-3 text-xs text-ovation-muted">
            Email: <strong className="text-ovation-ink">{invite.email}</strong>
          </div>

          <Field
            id="displayName"
            label="Your name"
            value={displayName}
            onChange={setDisplayName}
            required
            autoComplete="name"
          />
          <Field
            id="password"
            label="Choose a password"
            type="password"
            value={password}
            onChange={setPassword}
            required
            minLength={8}
            autoComplete="new-password"
            hint="At least 8 characters."
          />

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {submitting ? 'Accepting…' : 'Accept invitation'}
          </button>

          <div className="text-center text-xs text-ovation-muted">
            <Link to="/login" className="hover:text-ovation-ink">
              I already have an account
            </Link>
          </div>
        </form>
      )}
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-b from-ovation-paper to-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {children}
      </div>
    </div>
  )
}

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  autoComplete?: string
  required?: boolean
  minLength?: number
  hint?: string
}

function Field(props: FieldProps) {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-sm font-medium">
        {props.label}
      </label>
      <input
        id={props.id}
        type={props.type ?? 'text'}
        autoComplete={props.autoComplete}
        required={props.required}
        minLength={props.minLength}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
      />
      {props.hint && <p className="mt-1 text-xs text-ovation-muted">{props.hint}</p>}
    </div>
  )
}
